import { useEffect, useMemo, useState } from "react";
import useCountryLeagues from "@/hooks/useCountryLeagues";
import { useCompConfig } from "@/context/CompConfigContext";
import { buildThemeConfig } from "@/components/LichThiDauFull/buildThemeConfig";
import {
  fetchLtdMatchesForLeague,
  fetchKetquaMatchesForLeague,
  fetchMatchesForLeagueByDays,
} from "@/components/LichThiDauByLeague/leagueFixturesByLeagueActions";

function defaultLeagueId(leagues) {
  if (!leagues.length) return null;
  const hot = leagues.find((l) => Number(l?.isHot) === 1);
  const pick = hot || leagues[0];
  const id = pick?.id;
  return id != null ? String(id) : null;
}

/**
 * @param {string} countrySlug
 * @param {'tatca'|'ketqua'} [type]
 * @param {{ days?: number|null }} [options] — if `days` set, fetch by date window (no round)
 */
export default function useLichThiDauByCountry(countrySlug, type = "tatca", options = {}) {
  const days = options?.days != null ? Number(options.days) : null;
  const byDays = Number.isFinite(days) && days > 0;
  const isKetqua = type === "ketqua";
  const compConfig = useCompConfig();
  const themeStyle = buildThemeConfig(compConfig, compConfig.hasDataConfig);
  const [selectedId, setSelectedId] = useState(null);
  const [fixturesMap, setFixturesMap] = useState({});
  const [fixturesLoading, setFixturesLoading] = useState(false);

  const {
    country,
    leagues,
    loading: countryLoading,
    error: countryError,
  } = useCountryLeagues(countrySlug);

  useEffect(() => {
    setSelectedId(null);
    setFixturesMap({});
  }, [countrySlug]);

  useEffect(() => {
    if (!leagues.length) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) => {
      if (prev && leagues.some((l) => String(l?.id) === String(prev))) return prev;
      return defaultLeagueId(leagues);
    });
  }, [leagues]);

  const activeLeagues = useMemo(() => {
    if (!selectedId || !leagues.length) return [];
    const league = leagues.find((l) => String(l?.id) === String(selectedId));
    return league ? [league] : [];
  }, [leagues, selectedId]);

  const activeKey = activeLeagues.map((l) => String(l.id)).join(",");

  useEffect(() => {
    const leaguesToFetch = activeLeagues;
    if (!leaguesToFetch.length) {
      setFixturesMap({});
      setFixturesLoading(false);
      return undefined;
    }

    let cancelled = false;
    setFixturesLoading(true);

    (async () => {
      const entries = await Promise.all(
        leaguesToFetch.map(async (league) => {
          const rounds = byDays
            ? await fetchMatchesForLeagueByDays(league, { days, isKetqua })
            : await (isKetqua ? fetchKetquaMatchesForLeague : fetchLtdMatchesForLeague)(league);
          return [String(league.id), Array.isArray(rounds) ? rounds : []];
        }),
      );
      if (cancelled) return;
      const next = {};
      for (const [id, rounds] of entries) next[id] = rounds;
      setFixturesMap(next);
      setFixturesLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [activeKey, isKetqua, byDays, days]);

  const waiting = !!countrySlug && (countryLoading || fixturesLoading);
  const failed =
    !!countrySlug &&
    !countryLoading &&
    (!!countryError || !country || !leagues.length);

  const leaguesWithFixtures = activeLeagues.filter((league) => {
    const rounds = fixturesMap[String(league.id)];
    if (!Array.isArray(rounds) || !rounds.length) return false;
    return rounds.some(([, matches]) => (matches?.length ?? 0) > 0);
  });

  return {
    themeStyle,
    leagues,
    selectedId,
    setSelectedId,
    activeLeagues,
    fixturesMap,
    leaguesWithFixtures,
    waiting,
    failed,
    countryLoading,
  };
}
